import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { CreateRequest, ReservationType } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { QuoteBreakdown } from '../components/QuoteBreakdown';
import { CZK, TYPE_LABEL } from '../lib/format';

/**
 * Nová rezervace — the legacy reservations-edit form, new-booking half, in
 * its order: the product and the two dates first (they make the price), the
 * customer, the car, the services, the note; the price quoted live as the
 * legacy's `service.getprice` did on every date change, editable by the
 * desk as the legacy allowed; Uložit creates and opens the booking.
 *
 * What is not here yet, on purpose: the invoice block (Phase 1b payments),
 * the club member link, the partner fields, and "send e-mail" — the
 * customer e-mail is its own step in the roadmap.
 */
const kc = (n: number) => `${CZK.format(n)} Kč`;

function todayAt(h: number, plusDays = 0): { d: string; t: string } {
  const x = new Date();
  x.setDate(x.getDate() + plusDays);
  const p = (n: number) => String(n).padStart(2, '0');
  return { d: `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`, t: `${p(h)}:00` };
}

export function RezervaceNova() {
  const navigate = useNavigate();
  const a0 = todayAt(10, 1);
  const d0 = todayAt(18, 8);
  const [type, setType] = useState<ReservationType>('cheap');
  const [arrD, setArrD] = useState(a0.d);
  const [arrT, setArrT] = useState(a0.t);
  const [depD, setDepD] = useState(d0.d);
  const [depT, setDepT] = useState(d0.t);
  const [promo, setPromo] = useState('');
  const [pricelistId, setPricelistId] = useState<string>('');
  const [priceOverride, setPriceOverride] = useState<string>('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [car, setCar] = useState('');
  const [spz, setSpz] = useState('');
  const [flight, setFlight] = useState('');
  const [person, setPerson] = useState('0');
  const [note, setNote] = useState('');
  /* Valet parking (service 2, 0 Kč) is what puts a car on K převozu and
     the drivers' lists; nearly every Venku / Garáž booking has it (4 970
     of 5 033 closed in Sept–Oct 2026) and the old form offered it as the
     first checkbox. Ticked by default; the manager, 22 October 2026:
     "the only problem — the new dashboard has no Valet parking section". */
  const [services, setServices] = useState<Set<number>>(new Set([2]));

  const arrival = `${arrD} ${arrT}`;
  const departure = `${depD} ${depT}`;
  const datesOk = arrD && arrT && depD && depT && departure > arrival;

  const lists = useQuery({ queryKey: ['catalog', 'pricelists'], queryFn: () => api.pricelists(), staleTime: 10 * 60_000 });
  const catalog = useQuery({ queryKey: ['catalog', 'services'], queryFn: () => api.services(), staleTime: 10 * 60_000 });

  // The quote follows the form the way the legacy page re-asked getprice on
  // every change; debounced a touch so typing a promo does not fire six.
  const [quoteKey, setQuoteKey] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuoteKey(`${arrival}|${departure}|${type}|${promo}|${pricelistId}`), 250);
    return () => clearTimeout(t);
  }, [arrival, departure, type, promo, pricelistId]);
  const q = useQuery({
    queryKey: ['quote', quoteKey],
    queryFn: () =>
      api.quote({
        arrival,
        departure,
        type,
        pricelistId: pricelistId ? Number(pricelistId) : undefined,
        promo: promo.trim() || undefined,
        dynamic: true,
      }),
    enabled: !!datesOk && quoteKey !== '',
    retry: false,
  });

  const offered = useMemo(() => (catalog.data ?? []).filter((s) => s.canOrder[type]), [catalog.data, type]);
  const servicesTotal = offered.filter((s) => services.has(s.id)).reduce((t, s) => t + s.price, 0);
  const price = priceOverride.trim() !== '' ? Number(priceOverride) : (q.data?.price ?? 0);
  const priceOk = Number.isInteger(price) && price >= 0 && price <= 65_535;

  const m = useMutation({
    mutationFn: () => {
      const body: CreateRequest = {
        type,
        arrival,
        departure,
        driverName: name.trim(),
        driverPhone: phone.trim(),
        driverEmail: email.trim(),
        car: car.trim(),
        spz: spz.trim(),
        flightNumber: flight.trim(),
        note: note.trim(),
        person: Number(person) || 0,
        ...(pricelistId ? { pricelistId: Number(pricelistId) } : {}),
        ...(promo.trim() ? { promo: promo.trim() } : {}),
        ...(priceOverride.trim() !== '' ? { price } : {}),
        // Only what this product may order: a tick kept from another product is not sent.
        services: offered.filter((s) => services.has(s.id)).map((s) => s.id),
      };
      return api.createReservation(body);
    },
    onSuccess: (rc) => navigate(`/rezervace/${rc.id}`),
  });

  const err = m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message)
      : err instanceof Error
        ? err.message
        : null;

  const canSave = !!datesOk && name.trim() !== '' && priceOk && !m.isPending && !q.isPending;

  return (
    <div className="container">
      <div className="pagehead">
        <h1>Nová rezervace</h1>
        <span className="sub">telefonická nebo osobní objednávka</span>
      </div>

      <form
        className="card detailcard novaform"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) m.mutate();
        }}
      >
        <h2>Termín a produkt</h2>
        <div className="efgrid">
          <label className="ef">
            Produkt
            <select value={type} onChange={(e) => setType(e.target.value as ReservationType)} autoFocus>
              {(['cheap', 'regular', 'exclusive'] as const).map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="ef">
            Příjezd
            <span className="dt">
              <input type="date" value={arrD} onChange={(e) => setArrD(e.target.value)} required />
              <input type="time" value={arrT} onChange={(e) => setArrT(e.target.value)} required />
            </span>
          </label>
          <label className="ef">
            Odjezd
            <span className="dt">
              <input type="date" value={depD} onChange={(e) => setDepD(e.target.value)} required />
              <input type="time" value={depT} onChange={(e) => setDepT(e.target.value)} required />
            </span>
          </label>
          <label className="ef">
            Promo kód
            <input value={promo} onChange={(e) => setPromo(e.target.value)} maxLength={63} placeholder="např. LETO10" />
          </label>
          <label className="ef">
            Ceník
            <select value={pricelistId} onChange={(e) => setPricelistId(e.target.value)}>
              <option value="">podle promo kódu / základní</option>
              {(lists.data ?? []).map((l) => (
                <option key={l.id} value={String(l.id)}>
                  #{l.id} {l.title}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="quotebox" aria-live="polite">
          {!datesOk ? (
            <span className="empty">Odjezd musí být po příjezdu.</span>
          ) : q.isPending ? (
            <span className="empty">Počítám cenu…</span>
          ) : q.isError ? (
            <span className="arrive-error">Cenu se nepodařilo spočítat.</span>
          ) : q.data ? (
            <>
              <span>
                <b>{q.data.days}</b> {q.data.days === 1 ? 'den' : q.data.days < 5 ? 'dny' : 'dní'} · ceník #{q.data.pricelistId}{' '}
                {q.data.pricelistTitle}
                {q.data.promo ? ` (${q.data.promo})` : ''}
              </span>
              <span>
                parkování <b>{kc(q.data.price)}</b>
                {q.data.factor !== 1 && (
                  <span className="was">
                    {' '}
                    = {kc(q.data.base)} × {q.data.factor} (volných míst {q.data.remaining ?? '—'})
                  </span>
                )}
                {q.data.prevPrice != null && <span className="was"> · základní ceník {kc(q.data.prevPrice)}</span>}
              </span>
              {q.data.v2 && <QuoteBreakdown q={q.data} />}
              {!q.data.inPeriod && <span className="arrive-error">Promo ceník neplatí pro tento příjezd — cena je dvojnásobná.</span>}
              {(q.data.closed.arrival || q.data.closed.departure) && (
                <span className="arrive-error" role="alert">
                  Uzavírka: {q.data.closed.arrival && q.data.closed.departure ? 'příjezdy i odjezdy jsou' : q.data.closed.arrival ? 'příjezdy jsou' : 'odjezdy jsou'} v ten den
                  zavřené (Nastavení → Uzavírky). Web tento termín nepřijme; přepážka rezervovat může.
                </span>
              )}
            </>
          ) : null}
        </div>

        <h2>Klient a vůz</h2>
        <div className="efgrid">
          <label className="ef">
            Jméno
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={255} required />
          </label>
          <label className="ef">
            Telefon
            <input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={255} />
          </label>
          <label className="ef">
            E-mail
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} />
          </label>
          <label className="ef">
            Osob
            <input inputMode="numeric" pattern="[0-9]{0,2}" maxLength={2} value={person} onChange={(e) => setPerson(e.target.value)} />
          </label>
          <label className="ef">
            Auto
            <input value={car} onChange={(e) => setCar(e.target.value)} maxLength={255} />
          </label>
          <label className="ef">
            SPZ
            <input value={spz} onChange={(e) => setSpz(e.target.value)} maxLength={12} />
          </label>
          <label className="ef">
            Číslo letu
            <input value={flight} onChange={(e) => setFlight(e.target.value)} maxLength={32} />
          </label>
          <label className="ef wide">
            Poznámka
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} />
          </label>
        </div>

        <h2>Služby</h2>
        <div className="roster">
          {offered.length === 0 && <span className="empty">Pro tento produkt nejsou žádné služby.</span>}
          {offered.map((s) => (
            <label key={s.id} className="rosterbox">
              <input
                type="checkbox"
                checked={services.has(s.id)}
                onChange={() => {
                  const next = new Set(services);
                  if (next.has(s.id)) next.delete(s.id);
                  else next.add(s.id);
                  setServices(next);
                }}
              />{' '}
              {s.title} <span className="was">{s.price > 0 ? kc(s.price) : 'v ceně'}</span>
            </label>
          ))}
        </div>

        <h2>Cena</h2>
        <div className="efgrid">
          <label className="ef">
            Parkování (Kč)
            <input
              type="number"
              min={0}
              max={65535}
              step={1}
              value={priceOverride !== '' ? priceOverride : q.data ? String(q.data.price) : ''}
              onChange={(e) => setPriceOverride(e.target.value)}
              placeholder="podle ceníku"
            />
          </label>
          <div className="ef">
            Celkem
            <span className="total">{priceOk ? kc(price + servicesTotal) : '—'}</span>
          </div>
        </div>

        <div className="efactions">
          <button type="submit" className="btn primary" disabled={!canSave}>
            {m.isPending ? 'Ukládám…' : 'Uložit rezervaci'}
          </button>
          <button type="button" className="btn" onClick={() => navigate('/rezervace')} disabled={m.isPending}>
            Zrušit
          </button>
          {message && (
            <span className="arrive-error" role="alert">
              {message}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
